import React, { useState } from "react";
import {
    Search,
    BookOpen,
    Download,
    Book,
    RefreshCw,
    Database,
    Server,
    Wrench,
    FileText,
    Code,
    HelpCircle,
    ArrowRight,
    ChevronRight,
    PlayCircle,
    Users,
    Monitor,
} from "lucide-react";
import DemoRequestModal from "@/components/DemoRequestModal";
import { TopBar, FooterBar, GradientOrbVisual } from "@/components/marketing/Chrome";

/**
 * Documentation — public docs hub at /documentation. Distinct from the
 * existing in-app /docs route (protected, for authenticated users); this
 * page is meant to be reachable by anonymous visitors, which /docs isn't
 * (it lives inside ProtectedRoute and redirects to login).
 *
 * Only "Getting Started" has real content, matching the reference this was
 * built from -- the other sidebar items (Installation, User Guide, etc.)
 * are listed as a visual index of what the docs cover, not live links to
 * pages that don't exist yet, so nothing here is a dead link pretending to
 * be real content.
 */
export default function Documentation() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const openContact = () => setContactOpen(true);

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-[#050e22] text-white" data-testid="documentation-page">
            <div className="pointer-events-none fixed inset-0 grid-bg opacity-[0.1]" />

            <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} openContact={openContact} />
            <DocsHero />
            <DocsMain openContact={openContact} />
            <MoreResources />
            <ContactBanner openContact={openContact} />
            <FooterBar openContact={openContact} />
            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

function DocsHero() {
    return (
        <section className="relative z-10 overflow-hidden border-b border-white/5 px-6 pb-16 pt-14 sm:px-6 sm:pt-16" data-testid="docs-hero">
            <div className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[640px] rounded-full bg-[#0066ff]/25 blur-[140px]" />
            <div className="relative mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[1.15fr_1fr] lg:items-center">
                <div>
                    <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#3fb8ff]">Documentation</div>
                    <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl">
                        Guides, references, and <span className="text-[#3fb8ff]">resources.</span>
                    </h1>
                    <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-white/65">
                        Everything you need to plan, deploy, and use MoovBytes successfully.
                    </p>
                    <div className="mt-7 flex max-w-lg items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-4 py-3">
                        <Search size={16} className="shrink-0 text-white/40" />
                        <input
                            type="text"
                            placeholder="Search documentation, guides, or topics..."
                            className="w-full bg-transparent text-[13px] text-white placeholder:text-white/35 focus:outline-none"
                        />
                        <span className="shrink-0 rounded border border-white/15 px-1.5 py-0.5 font-mono text-[10px] text-white/40">Ctrl + K</span>
                    </div>
                </div>
                <GradientOrbVisual
                    heading={<>Move forward<br />with knowledge.</>}
                    body="Practical guidance for real-world Windows migrations."
                />
            </div>
        </section>
    );
}

const SIDEBAR_NAV = [
    { label: "Getting Started", icon: BookOpen, active: true },
    { label: "Installation", icon: Download },
    { label: "User Guide", icon: Book },
    { label: "Migration Guide", icon: RefreshCw },
    { label: "Supported Storage", icon: Database },
    { label: "Deployment", icon: Server },
    { label: "Troubleshooting", icon: Wrench },
    { label: "Release Notes", icon: FileText },
    { label: "API Reference", icon: Code },
];

const GETTING_STARTED = [
    { icon: PlayCircle, title: "Quick Start Guide", body: "Get up and running in minutes with a simple step-by-step overview.", tags: ["New Users", "5 min read"] },
    { icon: Monitor, title: "System Requirements", body: "Supported Windows versions, hardware, and environment details.", tags: ["Planning", "Reference"] },
    { icon: Download, title: "Installation Guide", body: "Install MoovBytes on workstations or servers.", tags: ["IT Administrators", "Step-by-step"] },
    { icon: RefreshCw, title: "Your First Migration", body: "Walk through a complete migration from source to destination.", tags: ["Hands-on Guide", "15 min read"] },
    { icon: PlayCircle, title: "Video Tutorials", body: "Watch short, practical videos on key tasks and features.", tags: ["How-to Videos", "On-demand"] },
    { icon: BookOpen, title: "Knowledge Base", body: "Articles, FAQs, and best practices for common scenarios.", tags: ["Troubleshooting", "Expert Tips"] },
];

function DocsMain({ openContact }) {
    return (
        <section className="relative z-10 px-6 py-16 sm:px-6" data-testid="docs-main">
            <div className="mx-auto grid max-w-[1600px] gap-10 lg:grid-cols-[280px_1fr]">
                <aside className="lg:sticky lg:top-24 lg:self-start">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#3fb8ff]">Documentation</div>
                    <nav className="mt-3 space-y-1">
                        {SIDEBAR_NAV.map((n) => (
                            <div
                                key={n.label}
                                className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-[13px] font-medium ${
                                    n.active ? "bg-gradient-to-r from-[#00C2FF]/20 to-[#0066ff]/20 text-white" : "text-white/60"
                                }`}
                            >
                                <span className="flex items-center gap-2.5">
                                    <n.icon size={15} className={n.active ? "text-[#3fb8ff]" : "text-white/40"} />
                                    {n.label}
                                </span>
                                <ChevronRight size={13} className="text-white/25" />
                            </div>
                        ))}
                    </nav>
                    <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                        <HelpCircle size={18} className="text-[#3fb8ff]" />
                        <div className="mt-2 text-[13px] font-bold text-white">Still need help?</div>
                        <p className="mt-1 text-[12px] leading-relaxed text-white/55">Visit our Support Centre for additional resources.</p>
                        <button
                            type="button"
                            onClick={openContact}
                            className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-[12px] font-medium text-white/80 transition hover:border-white/30 hover:text-white"
                        >
                            Contact Support <ArrowRight size={12} />
                        </button>
                    </div>
                </aside>

                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">Getting Started</div>
                    <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Get up and running quickly.</h2>
                    <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
                        New to MoovBytes? Start with these essential resources to plan your migration and get the most out of the platform.
                    </p>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2">
                        {GETTING_STARTED.map((c) => (
                            <div key={c.title} className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.025] p-5 transition hover:border-[#00C2FF]/30 hover:bg-white/[0.04]">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#00C2FF]/30 bg-[#00C2FF]/10 text-[#3fb8ff]">
                                    <c.icon size={18} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[14px] font-bold text-white">{c.title}</div>
                                    <p className="mt-1 text-[12.5px] leading-relaxed text-white/60">{c.body}</p>
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {c.tags.map((t) => (
                                            <span key={t} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/55">{t}</span>
                                        ))}
                                    </div>
                                </div>
                                <ArrowRight size={15} className="mt-1 shrink-0 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-[#3fb8ff]" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

const MORE_RESOURCES = [
    { icon: FileText, title: "Product Documentation", body: "Comprehensive guides and technical references.", link: "Browse Documentation" },
    { icon: Code, title: "API Reference", body: "Integrate MoovBytes into your workflows.", link: "View API Docs" },
    { icon: FileText, title: "Release Notes", body: "See what's new in the latest versions.", link: "View Release Notes" },
    { icon: Users, title: "Community & Support", body: "Find answers, share knowledge, and get help from our team.", link: "Visit Support" },
];

function MoreResources() {
    return (
        <section className="relative z-10 border-t border-white/5 bg-white/[0.015] px-6 py-20 sm:px-6" data-testid="docs-more-resources">
            <div className="mx-auto max-w-[1600px]">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#3fb8ff]">More Resources</div>
                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Additional resources for your migration journey.</h2>
                <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
                    Dive deeper with technical references, release information, and integration details.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {MORE_RESOURCES.map((r) => (
                        <div key={r.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                            <r.icon size={20} className="text-[#3fb8ff]" />
                            <div className="mt-3 text-[13.5px] font-bold text-white">{r.title}</div>
                            <p className="mt-1 text-[12px] leading-relaxed text-white/55">{r.body}</p>
                            <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-[#3fb8ff]">
                                {r.link} <ArrowRight size={11} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ContactBanner({ openContact }) {
    return (
        <section className="relative z-10 px-6 py-10 sm:px-6" data-testid="docs-contact-banner">
            <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:flex-row sm:items-center">
                <div>
                    <div className="text-[14px] font-bold text-white">Can't find what you're looking for?</div>
                    <p className="mt-1 text-[12.5px] text-white/60">Our support team is here to help. Get in touch and we'll make sure you have what you need.</p>
                </div>
                <button
                    type="button"
                    onClick={openContact}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2.5 text-[13px] font-medium text-white/85 transition hover:border-white/30 hover:text-white"
                >
                    Contact Support <ArrowRight size={13} />
                </button>
            </div>
        </section>
    );
}
